import { v2Api } from './v2Client';
import { MANAGER_PERMISSIONS } from '../../contexts/tournamentManagers';

const post = (url, body) => v2Api.post(url, body ?? {}).then((r) => r.data);
const items = (data) => (Array.isArray(data?.items) ? data.items : []);

/**
 * Équipe d'un tournoi du club (API TournamentStaffController, docs/TOURNAMENT_MANAGERS.md) : tournoi créé dans l'app
 * (`{ tournament_id }`, non officiel) ou importé (`{ match_import_id }`). Les officiels de la Ligue gardent
 * officialTournament.service (`/OfficialTournament/staff_*`).
 */

/** Libellés français des droits, dans l'ordre de l'API. */
export const TEAM_PERMISSION_LABELS = {
  schedule: 'Horaires',
  courts: 'Terrains',
  checkin: 'Pointage',
  referee: 'Arbitrage',
  results: 'Scores',
  draw: 'Tableaux',
  manage_staff: 'Équipe',
};

export const TEAM_PERMISSION_HINTS = {
  schedule: 'Date, terrain, arbitre et joueurs d’un match à venir, replanification',
  courts: 'Terrains des tableaux, blocages',
  checkin: 'Pointage, forfait le jour J',
  referee: 'Lancer / arrêter un match, arbitrage live, incidents',
  results: 'Encoder ou corriger un score, annuler un résultat',
  draw: 'Têtes de série, tableaux, poules',
  manage_staff: 'Gérer l’équipe du tournoi',
};

export const TEAM_ROLE_LABELS = { responsable: 'Responsable', adjoint: 'Adjoint' };

/** Droits réservés au responsable (jamais accordés par un responsable non admin). */
export const RESPONSABLE_ONLY_PERMISSIONS = ['draw', 'manage_staff'];

const ADJOINT_DEFAULT = MANAGER_PERMISSIONS.filter((p) => !RESPONSABLE_ONLY_PERMISSIONS.includes(p));

/**
 * Profils proposés à l'ajout : Gestionnaire (adjoint par défaut), Arbitrage & scores, Responsable (admin du club
 * seulement), Personnalisé (droits cochés, adjoint).
 */
export const TEAM_PRESETS = [
  { value: 'manager', label: 'Gestionnaire', role: 'adjoint', permissions: ADJOINT_DEFAULT, hint: 'Tout sauf les tableaux et l’équipe' },
  { value: 'referee', label: 'Arbitrage & scores', role: 'adjoint', permissions: ['referee', 'results'], hint: 'Lancer, arbitrer et encoder les matchs' },
  { value: 'responsable', label: 'Responsable', role: 'responsable', permissions: [...MANAGER_PERMISSIONS], adminOnly: true, hint: 'Tous les droits, équipe comprise' },
  { value: 'custom', label: 'Personnalisé', role: 'adjoint', permissions: null, hint: 'Choisir les droits un par un' },
];

/** Droits triés dans l'ordre de l'API, sans doublon ni inconnu. */
export function sortTeamPermissions(perms) {
  const set = new Set((perms || []).map(String));
  return MANAGER_PERMISSIONS.filter((p) => set.has(p));
}

/** Profil correspondant à un rôle + des droits (sinon « Personnalisé »). */
export function presetOf(role, permissions) {
  const sorted = sortTeamPermissions(permissions).join(',');
  const found = TEAM_PRESETS.find((p) => p.permissions && p.role === role && sortTeamPermissions(p.permissions).join(',') === sorted);
  return found ? found.value : 'custom';
}

/**
 * Droits qu'un appelant peut accorder : tout pour l'admin du club (`callerPermissions === null`), sinon ses propres droits
 * sans les droits du responsable (règle de l'API, 403 sinon).
 */
export function grantablePermissions(callerPermissions) {
  if (callerPermissions === null || callerPermissions === undefined) return [...MANAGER_PERMISSIONS];
  return sortTeamPermissions(callerPermissions).filter((p) => !RESPONSABLE_ONLY_PERMISSIONS.includes(p));
}

/** Profils utilisables par un appelant (Responsable : admin du club seulement ; un profil exige des droits accordables). */
export function availablePresets(callerPermissions) {
  const isAdmin = callerPermissions === null || callerPermissions === undefined;
  const grantable = new Set(grantablePermissions(callerPermissions));
  return TEAM_PRESETS.filter((p) => (p.adminOnly ? isAdmin : !p.permissions || p.permissions.every((x) => grantable.has(x))));
}

/** Cible de l'API depuis un tournoi géré ou un lot de l'écran Matchs. */
export function teamTarget({ tournamentId = null, matchImportId = null } = {}) {
  if (matchImportId) return { match_import_id: String(matchImportId) };
  if (tournamentId) return { tournament_id: String(tournamentId) };
  return null;
}

const ERRORS = {
  tournament_forbidden: 'Tu n’as pas le droit de faire cette action sur ce tournoi.',
  already_staff: 'Ce joueur fait déjà partie de l’équipe.',
  player_not_found: 'Ce joueur n’est pas membre du club.',
  official_tournament: 'L’équipe d’un tournoi officiel se gère depuis la Ligue.',
};

/** Message d'une erreur de l'API (message de l'API d'abord : il précise le refus). */
export function tournamentStaffErrorMessage(error, fallback = 'Action impossible.') {
  const data = error?.response?.data;
  if (typeof data === 'string' && data) return data;
  return data?.message || ERRORS[data?.error] || error?.message || fallback;
}

export const tournamentStaffService = {
  /** Tournois gérés du club courant : `[{ id, tournament_id?, match_import_id?, name, role, permissions }]`. */
  my: () => v2Api.get('/TournamentStaff/my').then((r) => (Array.isArray(r.data?.tournaments) ? r.data.tournaments : [])),
  /** Équipe : `[{ id, kind, player: { id, firstname, lastname, picture }, role, permissions, active, granted_at, note }]`. */
  list: (target) => post('/TournamentStaff/list', target).then(items),
  add: (target, { player_id, role, permissions, note } = {}) =>
    post('/TournamentStaff/add', { ...target, player_id, role, permissions, note: note || null }).then(items),
  /** `{ id, kind, role?, permissions?, active?, note? }`. */
  update: (body) => post('/TournamentStaff/update', body).then(items),
  remove: (id, kind) => post('/TournamentStaff/remove', { id, kind }).then(items),
};
